import Box from "@mui/material/Box";
import csiCard from "csi/card/_base";
import BackBanner from "designSystem/components/banner/BackBanner";
import FlexColumn from "designSystem/components/flex/FlexColumn";
import FlexTopDrawer from "designSystem/components/flex/FlexTopDrawer";
import def from "csi/def";
import { GalleryView } from "csi/gallery/View";
import DojoView from "csi/page/DojoView";
import LayoutTabs from "csi/page/LayoutTabs";
import { useDataSetupStore } from "csi/state/datasetup";
import { useStore } from "csi/state/hooks";
import { getLocationForUrl } from "csi/utility/routerUtility";
import { observer } from "mobx-react-lite";
import React, { useCallback, useState } from "react";
import DataSetupBreadcrumb from "./DataSetupBreadcrumb";
import DataSetupInstance from "./DataSetupInstance";
import DataSetupItem from "./DataSetupItem";
import { C8AttributeKey } from "csi/typings";

export const DataSetupContent = observer(() => {
	const [dynamicHeight, setDynamicHeight] = useState<number>(0);
	const dataSetupStore = useDataSetupStore();
	const { browserStore, localStore, routerStore } = useStore();
	const { fullScreen } = browserStore;
	const { activeItem, returnUrl } = dataSetupStore;
	const { locationUrl, renderDataSetupInstance } = routerStore;
	const { id, nodeType, subtypeUrl, url, viewId, viewOwnerType, prefKey } = activeItem;
	
	let defView: GalleryView;
	let attributeKeys: C8AttributeKey[];

	// use ref because top portion height is dynamic, and cannot use "auto" height for FlexTopDrawer transition to be smooth
	// NOTE: <Box {...{ ref: topRef }}> notation is necessary as "ref" is not a valid option
	// https://github.com/mui-org/material-ui/issues/17010
	const topRef = useCallback((node: HTMLElement) => {
		setDynamicHeight(node?.offsetHeight || 0);
	}, [locationUrl, activeItem, fullScreen]);

	if (localStore.dataSetup.viewItemAsGallery(id)) {
		const imageKey = csiCard.getAttributeCardImageKey(nodeType);
		attributeKeys = csiCard.getAttributeCardDetailedKeys(nodeType, true).map(String);
		defView = def.convertTableToGalleryView(viewOwnerType, viewId, imageKey, activeItem.subtypeUrl);
	}

	// NOTE: minHeight of 700 is to enforce a minimum page height of approx 768, when you combine it with the page header and footer

	return (
		<FlexTopDrawer
			heightClosed={0}
			heightOpen={dynamicHeight || undefined}
			hideSplitter
			minHeight={700}
			open={!fullScreen}
			renderWhenClosed
		>
			{{
				top:
					<Box {...{ ref: topRef }}>
						{returnUrl && (
							<BackBanner
								href={getLocationForUrl(returnUrl)}
								name={routerStore.getNodeLocationName(returnUrl)}
							/>
						)}
						<Box mx={4}>
							<Box py={4}>
								<DataSetupBreadcrumb />
							</Box>
							{renderDataSetupInstance ?
								<DataSetupInstance />
								:
								<DataSetupItem />
							}
						</Box>
					</Box>,

				bottom: renderDataSetupInstance ?
					<LayoutTabs />
					: (
						<FlexColumn px={4} py={4}>
							<DojoView
								fullHeight
								nodeUrl={url}
								subtypeUrl={subtypeUrl}
								viewId={viewId}
								viewParams={{ attributeKeys, defView, prefKey }}
							/>
						</FlexColumn>
					) }}
		</FlexTopDrawer>
	);
});

export default DataSetupContent;
